// Device detection utilities for cross-platform compatibility

// Cache device info result to avoid redundant detection
interface DeviceInfo {
  isIOS: boolean;
  isAndroid: boolean;
  isWindows: boolean;
  isMac: boolean;
  isChrome: boolean;
  isFirefox: boolean;
  isEdge: boolean;
  isSamsungBrowser: boolean;
  isOpera: boolean;
  isSafari: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isIPad: boolean;
  isIPod: boolean;
  hasTouch: boolean;
  supportsWebRTC: boolean;
  supportsWebGL: boolean;
  supportsWebAudio: boolean;
  screenWidth: number;
  screenHeight: number;
  pixelRatio: number;
  orientation: string;
}

let deviceInfoCache: DeviceInfo | null = null;
const checkWebGLSupport = (): boolean => {
  return true; // To accelerate app start!
};

export const getDeviceInfo = (): DeviceInfo => {
  if (deviceInfoCache !== null) return deviceInfoCache;

  const userAgent = navigator.userAgent;
  
  // Operating Systems (using userAgent since navigator.platform is deprecated)
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(userAgent);
  const isWindows = /Windows|Win/.test(userAgent);
  const isMac = /Macintosh|Mac OS X/.test(userAgent) && navigator.maxTouchPoints === 0;
  
  // Browsers (check order matters - check specific patterns first)
  const isChrome = /Chrome/.test(userAgent) || /CriOS/.test(userAgent);
  const isFirefox = /Firefox/.test(userAgent) || /FxiOS/.test(userAgent);
  const isEdge = /Edg/.test(userAgent);
  const isSamsungBrowser = /SamsungBrowser/.test(userAgent);
  const isOpera = /OPR/.test(userAgent) || /Opera/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent) && !/CriOS/.test(userAgent) && !/FxiOS/.test(userAgent) && !/Edg/.test(userAgent) && !/OPR/.test(userAgent) && !/Opera/.test(userAgent) && !/SamsungBrowser/.test(userAgent);
  
  // Device types (computed from OS detection)
  const isMobile = (/Mobi/i.test(userAgent) || isIOS || isAndroid) && !/iPad/.test(userAgent);
  const isTablet = /iPad/.test(userAgent) || (isAndroid && !/Mobi/.test(userAgent)) || (navigator.maxTouchPoints > 1 && !isMac && !isWindows && !isIOS);
  const isIPad = /iPad/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isIPod = /iPod/.test(userAgent);
  
  const deviceInfo = {
    // Operating Systems
    isIOS,
    isAndroid,
    isWindows,
    isMac,
    
    // Browsers
    isChrome,
    isFirefox,
    isEdge,
    isSamsungBrowser,
    isOpera,
    isSafari,
    
    // Device types (computed from OS detection)
    isMobile,
    isTablet,
    isIPad,
    isIPod,
    
    // Capabilities
    hasTouch: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    supportsWebRTC: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
    supportsWebGL: checkWebGLSupport(), // Use cached check to avoid creating multiple WebGL contexts
    supportsWebAudio: !!(window.AudioContext || (window as any).webkitAudioContext),
    
    // Screen info
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    pixelRatio: window.devicePixelRatio || 1,
    orientation: window.screen.orientation?.type || (window.innerWidth > window.innerHeight ? 'landscape' : 'portrait')
  };

  deviceInfoCache = deviceInfo;
  return deviceInfo;
};

export const getPlatformName = () => {
  const device = getDeviceInfo();
  if (device.isIOS) return 'iOS';
  if (device.isAndroid) return 'Android';
  if (device.isWindows) return 'Windows';
  if (device.isMac) return 'macOS';
  return 'Unknown';
};

export const getBrowserName = () => {
  const device = getDeviceInfo();
  if (device.isEdge) return 'Edge';
  if (device.isChrome) return 'Chrome';
  if (device.isSafari) return 'Safari';
  if (device.isFirefox) return 'Firefox';
  if (device.isSamsungBrowser) return 'Samsung Browser';
  if (device.isOpera) return 'Opera';
  return 'Unknown';
};

/**
 * Check if setSinkId (audio output device selection) is supported and usable.
 * Safari exposes setSinkId but requires a user gesture for every call.
 * When Daily.co stores a device preference it applies setSinkId to every
 * new internal audio element, causing hundreds of repeated
 * "NotAllowedError: A user gesture is required" errors on Safari.
 */
export const isSinkIdSupported = (): boolean => {
  const audio = document.createElement('audio');
  if (typeof (audio as any).setSinkId !== 'function') return false;
  // Safari and iOS browsers expose setSinkId but require a user gesture
  // for each call — disable to prevent repeated NotAllowedError spam
  const device = getDeviceInfo();
  if (device.isSafari || device.isIOS) return false;
  return true;
};
